// src/pages/Admin/DossierInscription/DossierInscriptionPage.jsx

import React, { useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaFileExcel,
  FaFolderOpen,
  FaPlus,
  FaRedo,
  FaTimesCircle,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import useDossiersInscriptionAdmin from "./useDossiersInscriptionAdmin";

import {
  formatDateFr,
  getAdherentName,
  getDossierCompletion,
  getRoleId,
  getStoredUserInfo,
  getGroupsText,
} from "./dossier.helpers";

import DossierFiltersBar from "./components/DossierFiltersBar";
import DossiersTable from "./components/DossiersTable";
import CreateDossierInscriptionDrawer from "./components/CreateDossierInscriptionDrawer";
import EditDossierInscriptionDrawer from "./components/EditDossierInscriptionDrawer";

function MiniStat({ icon, label, value, helper, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    rose: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
            {label}
          </p>

          <p className="mt-0.5 text-xl font-bold text-slate-950 dark:text-white">
            {Number(value || 0).toLocaleString("fr-FR")}
          </p>

          {helper ? (
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
              {helper}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Dossiers d'inscription
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function DossierInscriptionPage() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user || storedUser);

  const { state, setters, handlers } = useDossiersInscriptionAdmin();

  const {
    loading,
    seasons,
    groups,
    searchTerm,
    seasonFilter,
    selectedGroup,
    validateCmFilter,
    validateLicenceFilter,
    validateReglementFilter,
    sortField,
    sortOrder,
    visibleCount,
    scrollRef,
    filtered,
    visibleDossiers,
    fsFlags,
    folderCreating,
    duplicateWarning,
    stats,
  } = state;

  const {
    setSearchTerm,
    setSeasonFilter,
    setSelectedGroup,
    setValidateCmFilter,
    setValidateLicenceFilter,
    setValidateReglementFilter,
  } = setters;

  const {
    fetchDossiers,
    fetchInitialData,
    onScroll,
    toggleValidate,
    handleEnsureFolder,
    deleteDossier,
    isRowRefreshing,
    toggleNameSort,
    toggleCompletionSort,
    resetFilters,
  } = handlers;

  const [drawer, setDrawer] = useState({
    type: null,
    dossier: null,
  });

  /* -------------------------------------------------------------------------- */
  /*                      Emails des adhérents filtrés                         */
  /* -------------------------------------------------------------------------- */

  const filteredEmails = useMemo(() => {
    const seen = new Set();

    return filtered.reduce((emails, dossier) => {
      const email = String(dossier?.Adherent?.email || "").trim();

      if (!email) return emails;

      const normalizedEmail = email.toLowerCase();

      // On ignore les valeurs manifestement incorrectes
      if (!normalizedEmail.includes("@")) return emails;

      // On évite les doublons
      if (seen.has(normalizedEmail)) return emails;

      seen.add(normalizedEmail);
      emails.push(email);

      return emails;
    }, []);
  }, [filtered]);

  const copyFilteredEmails = async () => {
    if (!filteredEmails.length) {
      toast.info("Aucune adresse email à copier avec les filtres actuels.");
      return;
    }

    const emailsText = filteredEmails.join(";");

    try {
      // Méthode moderne
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(emailsText);
      } else {
        // Fallback notamment pour certains environnements HTTP
        const textarea = document.createElement("textarea");

        textarea.value = emailsText;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        textarea.style.top = "-9999px";

        document.body.appendChild(textarea);

        textarea.focus();
        textarea.select();

        const copied = document.execCommand("copy");

        document.body.removeChild(textarea);

        if (!copied) {
          throw new Error("La copie dans le presse-papiers a échoué.");
        }
      }

      toast.success(
        `${filteredEmails.length} adresse${
          filteredEmails.length > 1 ? "s" : ""
        } email copiée${filteredEmails.length > 1 ? "s" : ""}.`,
      );
    } catch (error) {
      console.error("Erreur copie emails :", error);
      toast.error("Impossible de copier les adresses email.");
    }
  };

  const exportExcel = () => {
    const rows = filtered.map((dossier) => {
      const completion = getDossierCompletion(dossier);
      const adherent = dossier?.Adherent || {};

      return {
        Nom: adherent?.nom || "",
        Prénom: adherent?.prenom || "",
        Email: adherent?.email || "",
        Saison: adherent?.saison_active || "",
        Groupes: getGroupsText(dossier),
        "CM validé": dossier?.validate_cm ? "Oui" : "Non",
        "Date CM": dossier?.date_cm ? formatDateFr(dossier.date_cm) : "",
        "Licence validée": dossier?.validate_licence ? "Oui" : "Non",
        "Règlement validé": dossier?.validate_reglement ? "Oui" : "Non",
        Avancement: `${completion.done}/${completion.total}`,
        "URL CM": dossier?.cm_url || "",
        "Formulaire à signer": dossier?.formulaire_licence_a_signer || "",
        "Formulaire signé":
          dossier?.formulaire_licence_signe ||
          dossier?.formulaire_licence_signee ||
          "",
        "URL Licence":
          dossier?.licence_url || dossier?.licence_assurance_url || "",
        "Lien Drive": dossier?.lien_drive || "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Dossiers");

    const buffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `dossiers_inscription_${seasonFilter || "toutes_saisons"}.xlsx`,
    );
  };

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <div className="relative z-40 overflow-visible rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Administration
                </p>

                <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
                  Dossiers d'inscription
                </h1>

                <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Suivez les certificats médicaux, licences, règlements,
                  formulaires et dossiers serveur/Drive de chaque adhérent.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDrawer({ type: "create", dossier: null })}
                  className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700"
                >
                  <FaPlus />
                  Ajouter
                </button>

                <button
                  type="button"
                  onClick={exportExcel}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaFileExcel />
                  Excel
                </button>

                <button
                  type="button"
                  onClick={fetchInitialData}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaRedo className={loading ? "animate-spin" : ""} />
                  Actualiser
                </button>
              </div>
            </div>

            {duplicateWarning ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
                {duplicateWarning}
              </div>
            ) : null}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MiniStat
            icon={<FaFolderOpen />}
            label="Affichés"
            value={stats.affiches}
            helper={`${stats.total} dossier(s) au total`}
            accent="indigo"
          />

          <MiniStat
            icon={<FaCheckCircle />}
            label="Complets"
            value={stats.complets}
            helper="CM + licence + règlement"
            accent="emerald"
          />

          <MiniStat
            icon={<FaCheckCircle />}
            label="CM validés"
            value={stats.cmOk}
            helper="Certificat médical"
            accent="amber"
          />

          <MiniStat
            icon={<FaTimesCircle />}
            label="À vérifier"
            value={Math.max(0, stats.affiches - stats.complets)}
            helper="Dossiers incomplets"
            accent="rose"
          />
        </div>

        <div className="relative z-10 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <DossierFiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            seasonFilter={seasonFilter}
            setSeasonFilter={setSeasonFilter}
            seasons={seasons}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            groups={groups}
            validateCmFilter={validateCmFilter}
            setValidateCmFilter={setValidateCmFilter}
            validateLicenceFilter={validateLicenceFilter}
            setValidateLicenceFilter={setValidateLicenceFilter}
            validateReglementFilter={validateReglementFilter}
            setValidateReglementFilter={setValidateReglementFilter}
            filteredCount={filtered.length}
            emailCount={filteredEmails.length}
            onCopyEmails={copyFilteredEmails}
            resetFilters={resetFilters}
          />

          <DossiersTable
            rows={visibleDossiers}
            totalCount={filtered.length}
            visibleCount={visibleCount}
            loading={loading}
            scrollRef={scrollRef}
            onScroll={onScroll}
            fsFlags={fsFlags}
            folderCreating={folderCreating}
            isRowRefreshing={isRowRefreshing}
            toggleValidate={toggleValidate}
            handleEnsureFolder={handleEnsureFolder}
            sortField={sortField}
            sortOrder={sortOrder}
            toggleNameSort={toggleNameSort}
            toggleCompletionSort={toggleCompletionSort}
            onEdit={(dossier) => setDrawer({ type: "edit", dossier })}
            onDelete={deleteDossier}
          />
        </div>
      </div>

      <CreateDossierInscriptionDrawer
        isOpen={drawer.type === "create"}
        onClose={() => setDrawer({ type: null, dossier: null })}
        refreshList={fetchDossiers}
      />

      <EditDossierInscriptionDrawer
        isOpen={drawer.type === "edit"}
        onClose={() => setDrawer({ type: null, dossier: null })}
        dossier={drawer.dossier}
        refreshList={fetchDossiers}
      />

      <ToastContainer />
    </section>
  );
}
